import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import CategoryDropdown from './components/CategoryDropdown';
import CategoryGrid from './components/CategoryGrid';
import JsonLd from '@/components/base/JsonLd';
import { featuredArticle, pillarArticles } from '@/mocks/articles';
import { MAIN_SITE_URL } from '@/mocks/categories';
import { SITE_URL, SITE_NAME, SITE_LOGO_URL } from '@/constants/site';

const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  description: 'Independent informational resource centered on the USFans Spreadsheet.',
};

const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  logo: SITE_LOGO_URL,
};

export default function Home() {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace('#', ''));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 60);
      }
    }
  }, [location.hash]);

  const faqItems = [
    { q: t('faq.q1'), a: t('faq.a1') },
    { q: t('faq.q2'), a: t('faq.a2') },
    { q: t('faq.q3'), a: t('faq.a3') },
    { q: t('faq.q4'), a: t('faq.a4') },
    { q: t('faq.q5'), a: t('faq.a5') },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <JsonLd data={websiteJsonLd} />
      <JsonLd data={organizationJsonLd} />

      <main>
        <Hero />

        {/* Categories */}
        <section id="categories" className="scroll-mt-20 bg-background-50">
          <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                {t('categories.title')}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                {t('categories.subtitle')}
              </p>
            </div>

            <div className="mt-8 flex justify-center">
              <CategoryDropdown />
            </div>

            <div className="mt-10">
              <CategoryGrid />
            </div>
          </div>
        </section>

        {/* Intro */}
        <section id="safety" className="scroll-mt-20 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
              <div className="order-2 lg:order-1">
                <span className="text-xs font-bold uppercase tracking-widest text-primary-600">
                  {t('intro.eyebrow')}
                </span>
                <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                  {t('intro.title')}
                </h2>
                <p className="mt-5 text-sm leading-relaxed text-foreground-700 md:text-base">
                  {t('intro.p1')}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                  {t('intro.p2')}
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <a
                    href={MAIN_SITE_URL}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
                  >
                    {t('intro.cta')}
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-arrow-right-up-line"></i>
                    </span>
                  </a>
                  <Link
                    to="/article/usfans-spreadsheet-guide"
                    className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-200 whitespace-nowrap"
                  >
                    {t('hero.ctaGuide')}
                  </Link>
                </div>
              </div>

              <div className="order-1 lg:order-2">
                <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20neat%20flat%20lay%20of%20a%20sage%20green%20hoodie%20and%20white%20sneakers%20arranged%20with%20a%20clipboard%20and%20pen%20on%20a%20soft%20cream%20desk%2C%20clean%20minimal%20product%20photography%2C%20soft%20natural%20daylight%2C%20subtle%20shadows%2C%20fresh%20editorial%20workspace%20aesthetic%2C%20high%20detail&width=800&height=800&seq=usfans-intro-new-03&orientation=squarish"
                    alt="USFans Spreadsheet buying guide"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Articles */}
        <section id="articles" className="scroll-mt-20 bg-background-50">
          <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary-600">
                  {t('articles.eyebrow')}
                </span>
                <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                  {t('articles.title')}
                </h2>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {t('articles.subtitle')}
                </p>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              <Link
                to="/article/usfans-spreadsheet-guide"
                className="group flex flex-col rounded-lg border border-primary-200 bg-primary-50 p-6 transition-all hover:-translate-y-0.5 hover:border-primary-400 md:col-span-3 lg:col-span-1"
              >
                <span className="inline-flex w-fit rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                  {t('articles.featured')}
                </span>
                <span className="mt-4 text-xs font-semibold uppercase tracking-wide text-primary-600">
                  {featuredArticle.category}
                </span>
                <h3 className="mt-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  {featuredArticle.title}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {featuredArticle.description}
                </p>
                <span className="mt-5 flex items-center gap-2 text-sm font-semibold text-primary-700">
                  {t('articles.readMore')}
                  <span className="flex h-4 w-4 items-center justify-center transition-transform group-hover:translate-x-0.5">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </span>
              </Link>

              {pillarArticles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/article/${a.slug}`}
                  className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-6 transition-all hover:-translate-y-0.5 hover:border-primary-300"
                >
                  <span className="inline-flex w-fit rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-800">
                    {a.category}
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-900 md:text-lg">
                    {a.title}
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-600">
                    {a.description}
                  </p>
                  <span className="mt-5 flex items-center gap-2 text-sm font-semibold text-primary-700">
                    {t('articles.readMore')}
                    <span className="flex h-4 w-4 items-center justify-center transition-transform group-hover:translate-x-0.5">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="scroll-mt-20 bg-background-100">
          <div className="mx-auto w-full max-w-3xl px-4 py-14 md:px-6 md:py-20">
            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-widest text-primary-600">
                {t('faq.eyebrow')}
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                {t('faq.title')}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                {t('faq.subtitle')}
              </p>
            </div>

            <div className="mt-8 space-y-3">
              {faqItems.map((item, i) => (
                <details
                  key={i}
                  className="group rounded-lg border border-background-200 bg-background-50"
                >
                  <summary className="flex cursor-pointer items-center justify-between gap-4 px-5 py-4 text-sm font-semibold text-foreground-900 md:text-base">
                    {item.q}
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center text-foreground-400 transition-transform group-open:rotate-45">
                      <i className="ri-add-line"></i>
                    </span>
                  </summary>
                  <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600">
                    {item.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden bg-background-50">
          <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <div className="rounded-lg bg-secondary-100 px-6 py-12 text-center md:px-12 md:py-16">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                {t('cta.title')}
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
                {t('cta.subtitle')}
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 md:text-base whitespace-nowrap"
                >
                  {t('cta.primary')}
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-up-line"></i>
                  </span>
                </a>
                <Link
                  to="/article/usfans-spreadsheet-guide"
                  className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-200 md:text-base whitespace-nowrap"
                >
                  {t('cta.secondary')}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}